// ── library.js — общая библиотека (перенесено из Palabra por palabra) ─────────
// shared_dict: быстрые переводы слов, общие для всех; один раз переведённое
//              слово больше никому не нужно переводить заново.
// word_cards:  подробные разборы слов от Claude, с автором и лайками.
// Таблицы создаёт supabase_migration_library.sql. Если их ещё нет, функции
// тихо возвращают пустой результат — сайт работает как раньше.

import { supabase } from './supabase';

import { t } from './i18n';
const key = (w) => String(w || '').trim().toLowerCase();
let _missing = false; // таблиц нет (миграция не запущена) — не дёргаем сервер зря
const isMissing = (error) => /does not exist|schema cache|PGRST20[05]/i.test(`${error?.message || ''} ${error?.code || ''}`);

// ── Быстрый словарь ──────────────────────────────────────────────────────────
const _dictMem = new Map(); // `${lang}|${xl}|${word}` → статья или null (уже искали, нет)

export function dictCached(lang, xl, word) {
  return _dictMem.get(`${lang}|${xl}|${key(word)}`);
}

// Найти переводы пачки слов. Возвращает Map(слово → статья).
export async function getSharedDict(lang, xl, words) {
  const out = new Map();
  const ask = [];
  for (const w of new Set(words.map(key).filter(Boolean))) {
    const hit = _dictMem.get(`${lang}|${xl}|${w}`);
    if (hit) out.set(w, hit);
    else if (hit === undefined) ask.push(w);
  }
  if (!ask.length || _missing) return out;
  for (let i = 0; i < ask.length; i += 150) {
    const chunk = ask.slice(i, i + 150);
    const { data, error } = await supabase.from('shared_dict').select('word,t').eq('lang', lang).eq('xl', xl).in('word', chunk);
    if (error) { if (isMissing(error)) _missing = true; else console.warn('[library] dict:', error.message); return out; }
    const found = new Set();
    for (const r of data || []) { _dictMem.set(`${lang}|${xl}|${r.word}`, r.t); out.set(r.word, r.t); found.add(r.word); }
    chunk.forEach((w) => { if (!found.has(w)) _dictMem.set(`${lang}|${xl}|${w}`, null); });
  }
  return out;
}

// Добавить переводы в общий словарь. Уже существующие не перезаписываются.
export async function addSharedDict(lang, xl, map) {
  const rows = [];
  for (const [w, t] of map) {
    const k = key(w);
    if (!k || k.length > 100) continue;
    _dictMem.set(`${lang}|${xl}|${k}`, t);
    rows.push({ lang, xl, word: k, t });
  }
  if (!rows.length || _missing) return;
  for (let i = 0; i < rows.length; i += 200) {
    const { error } = await supabase.from('shared_dict').upsert(rows.slice(i, i + 200), { onConflict: 'lang,xl,word', ignoreDuplicates: true });
    if (error) { if (isMissing(error)) _missing = true; else console.warn('[library] dict add:', error.message); return; }
  }
}

// ── Подробные разборы ────────────────────────────────────────────────────────
// Разборы слова: сначала свои, потом по лайкам. liked — ставил ли я лайк.
export async function listCards(lang, word, xl, myId) {
  if (_missing) return [];
  const { data, error } = await supabase.from('word_cards')
    .select('id,lang,xl,word,data,model,is_public,by,by_name,likes,created_at')
    .eq('lang', lang).eq('word', key(word)).eq('xl', xl)
    .order('likes', { ascending: false }).order('created_at').limit(30);
  if (error) { if (isMissing(error)) _missing = true; else console.warn('[library] cards:', error.message); return []; }
  const cards = data || [];
  let liked = new Set();
  if (cards.length && myId) {
    const { data: l } = await supabase.from('word_card_likes').select('card_id').eq('user_id', myId).in('card_id', cards.map((c) => c.id));
    liked = new Set((l || []).map((x) => x.card_id));
  }
  return cards.map((c) => ({ ...c, mine: c.by === myId, liked: liked.has(c.id) }))
    .sort((a, b) => (b.mine - a.mine) || (b.likes - a.likes));
}

export async function addCard({ lang, xl, word, data, model, isPublic }) {
  const { data: row, error } = await supabase.from('word_cards')
    .insert({ lang, xl, word: key(word), data, model: String(model || '').slice(0, 60), is_public: !!isPublic })
    .select('id,lang,xl,word,data,model,is_public,by,by_name,likes,created_at').single();
  if (error) throw new Error(isMissing(error) ? t("Библиотека ещё не создана в Supabase.") : error.message);
  return { ...row, mine: true, liked: false };
}

export async function updateCard(id, fields) {
  const { error } = await supabase.from('word_cards').update(fields).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteCard(id) {
  const { error } = await supabase.from('word_cards').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function setLike(cardId, on) {
  const q = on
    ? supabase.from('word_card_likes').insert({ card_id: cardId })
    : supabase.from('word_card_likes').delete().eq('card_id', cardId);
  const { error } = await q;
  if (error && !/duplicate/i.test(error.message)) throw new Error(error.message);
}

// ── Профиль: имя под разборами ───────────────────────────────────────────────
export async function getProfile(userId) {
  if (_missing || !userId) return null;
  const { data, error } = await supabase.from('profiles').select('id,name,plan,plan_until').eq('id', userId).maybeSingle();
  if (error) { if (isMissing(error)) _missing = true; return null; }
  return data;
}

export async function saveProfileName(userId, name) {
  const n = String(name || '').trim().slice(0, 60);
  const existing = await getProfile(userId);
  const { error } = existing
    ? await supabase.from('profiles').update({ name: n, last_seen: new Date().toISOString() }).eq('id', userId)
    : await supabase.from('profiles').insert({ id: userId, name: n });
  if (error) throw new Error(isMissing(error) ? t("Библиотека ещё не создана в Supabase.") : error.message);
}

// Отметка «был на сайте» для админ-панели — не чаще раза в день.
export async function touchProfile(userId) {
  if (!userId || _missing) return;
  try {
    const k = `ac_seen_${userId}`;
    if (localStorage.getItem(k) === new Date().toISOString().slice(0, 10)) return;
    const p = await getProfile(userId);
    if (_missing) return;
    const { error } = p
      ? await supabase.from('profiles').update({ last_seen: new Date().toISOString() }).eq('id', userId)
      : await supabase.from('profiles').insert({ id: userId });
    if (!error) localStorage.setItem(k, new Date().toISOString().slice(0, 10));
  } catch {}
}

// ── Админ ────────────────────────────────────────────────────────────────────
export async function isAdmin() {
  if (_missing) return false;
  const { data, error } = await supabase.rpc('is_admin');
  if (error) return false;
  return !!data;
}
export async function adminUsers() {
  const { data, error } = await supabase.rpc('admin_users');
  if (error) throw new Error(error.message);
  return data || [];
}
export async function adminSetPlan(userId, plan, until) {
  const { error } = await supabase.rpc('admin_set_plan', { target: userId, new_plan: plan, until: until || null });
  if (error) throw new Error(error.message);
}
// ── Админ: блокировка, «выкинуть», права, объявления, журнал (supabase_migration_admin.sql) ──
const needAdminSql = (error) => /function .* does not exist|PGRST202|admin_ban|admin_overview|admin_log_list|my_status|announcements/i.test(`${error.code} ${error.message}`);
const adminCall = async (fn, args) => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(needAdminSql(error) ? t("Нужно один раз выполнить supabase_migration_admin.sql в Supabase (SQL Editor).") : error.message);
  return data;
};
export const adminOverview = () => adminCall('admin_overview');
export const adminBan = (userId, until, reason) => adminCall('admin_ban', { target: userId, until: until || null, reason: reason || '' });
export const adminUnban = (userId) => adminCall('admin_unban', { target: userId });
export const adminKick = (userId) => adminCall('admin_kick', { target: userId });
export const adminDeleteUser = (userId) => adminCall('admin_delete_user', { target: userId });
export const adminSetAdmin = (userId, make) => adminCall('admin_set_admin', { target: userId, make: !!make });
export const adminAnnounce = (text, level = 'info') => adminCall('admin_announce', { msg: text || '', lvl: level });
export const adminLog = async (limit = 200) => (await adminCall('admin_log_list', { lim: limit })) || [];

// Объявление для всех (баннер на главной). Нет таблицы — просто ничего не показываем.
export async function getAnnouncement() {
  try {
    const { data, error } = await supabase.from('announcements').select('id,text,level').eq('active', true)
      .order('created_at', { ascending: false }).limit(1);
    if (error) return null;
    return data?.[0] || null;
  } catch { return null; }
}
// Статус своего аккаунта: заблокирован ли, когда админ «выкинул».
export async function myStatus() {
  try {
    const { data, error } = await supabase.rpc('my_status');
    if (error) return null;
    return data || null;
  } catch { return null; }
}

// Последние общие разборы — для модерации.
export async function adminRecentCards(limit = 50) {
  const { data, error } = await supabase.from('word_cards')
    .select('id,lang,xl,word,data,model,by_name,likes,is_public,created_at')
    .order('created_at', { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return data || [];
}

// Журнал ошибок приложения (supabase_migration_client_errors.sql, пишет src/errorLog.js).
const noErrorTable = (error) => /client_errors|does not exist|PGRST205|42P01/i.test(`${error.code} ${error.message}`);
export async function adminErrors(limit = 200) {
  const { data, error } = await supabase.from('client_errors')
    .select('id,created_at,user_id,platform,app_version,kind,message,stack,url,user_agent')
    .order('created_at', { ascending: false }).limit(limit);
  if (error) throw new Error(noErrorTable(error) ? t("Журнала ещё нет: выполните supabase_migration_client_errors.sql в Supabase.") : error.message);
  return data || [];
}
export async function adminClearErrors() {
  const { error } = await supabase.from('client_errors').delete().gt('id', 0);
  if (error) throw new Error(error.message);
}
